import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { 
  ArrowLeft, MapPin, Phone, AlertTriangle, User, 
  Heart, Activity, ChevronRight, Search
} from "lucide-react";

export default function MyClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const { data } = await api.get("/employee/clients");
      setClients(data || []);
    } catch (err) {
      console.error("Failed to load clients:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => 
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase())
  );

  const getRiskBadge = (fallRisk) => {
    if (fallRisk === "high") return { bg: "bg-red-50", text: "text-red-700", label: "High Fall Risk" };
    if (fallRisk === "moderate") return { bg: "bg-amber-50", text: "text-amber-700", label: "Moderate Risk" };
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-semibold text-lg">My Clients</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-2 focus:ring-[#06372B]/20 outline-none transition-all"
            data-testid="client-search"
          />
        </div>

        {/* Client List */}
        {filteredClients.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <div className="font-medium text-gray-700">No Clients Found</div>
            <div className="text-sm text-gray-500 mt-1">
              {search ? "Try a different search term" : "No clients assigned yet"}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredClients.map((client) => {
              const riskBadge = getRiskBadge(client.fall_risk);
              
              return (
                <Link
                  key={client.id}
                  to={`/employee/client/${client.id}`}
                  className="block bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow"
                  data-testid={`client-card-${client.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{client.first_name} {client.last_name}</h3>
                        {client.is_primary && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#06372B]/10 text-[#06372B]">
                            Primary
                          </span>
                        )}
                      </div>
                      
                      {client.address && (
                        <div className="flex items-start gap-1.5 mt-2 text-sm text-gray-500">
                          <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                          <span className="line-clamp-1">{client.address}</span>
                        </div>
                      )}
                      
                      {client.phone && (
                        <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                          <Phone className="w-4 h-4" />
                          <span>{client.phone}</span>
                        </div>
                      )}

                      {/* Health indicators */}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {riskBadge && (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${riskBadge.bg} ${riskBadge.text}`}>
                            <AlertTriangle className="w-3 h-3" />
                            {riskBadge.label}
                          </span>
                        )}
                        
                        {client.cognitive_status && client.cognitive_status !== "alert_oriented" && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                            <Activity className="w-3 h-3" />
                            {client.cognitive_status.replace(/_/g, " ")}
                          </span>
                        )}

                        {client.allergies?.length > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                            <Heart className="w-3 h-3" />
                            {client.allergies.length} Allerg{client.allergies.length > 1 ? "ies" : "y"}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
